import { useState, useEffect } from "react";
import { useConfirm } from "@/components/app/ConfirmDialog";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { deleteVisualsAndAssets } from "@/lib/deleteVisuals";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Package, Plus, Search, MoreHorizontal, Copy, Trash2, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";


interface ProductListItem {
  id: string;
  name: string;
  product_params: any;
  updated_at: string;
  project_id: string;
  scenario_count?: number;
  render_count?: number;
  thumbnailUrl: string | null;
}

export default function ProductLibrary() {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    if (workspace && user) {
      loadProducts();
    } else if (!workspace && user) {
      setLoading(false);
    }
  }, [workspace, user]);

  const loadProducts = async () => {
    if (!workspace) return;
    setLoading(true);

    const { data, error } = await supabase
      .from("products")
      .select("id, name, product_params, updated_at, project_id")
      .eq("workspace_id", workspace.id)
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("Error loading products:", error);
      setLoading(false);
      return;
    }

    const productIds = (data || []).map((p) => p.id);

    // Fetch primary image per product (falls back to oldest image if none marked primary)
    const { data: images } = productIds.length > 0
      ? await supabase
          .from("product_images")
          .select("product_id, storage_path, is_primary")
          .in("product_id", productIds)
          .order("is_primary", { ascending: false })
          .order("created_at", { ascending: true })
      : { data: [] };

    const thumbMap = new Map<string, string>();
    for (const img of images || []) {
      if (!thumbMap.has(img.product_id)) {
        const { data: urlData } = supabase.storage.from("product-images").getPublicUrl(img.storage_path);
        thumbMap.set(img.product_id, urlData.publicUrl);
      }
    }

    setProducts((data || []).map((p) => ({ ...p, thumbnailUrl: thumbMap.get(p.id) || null })));
    setLoading(false);
  };

  const deleteProduct = async (productId: string, projectId: string) => {
    const ok = await confirm({
      title: "Delete this product?",
      description: "Every visual made for it will be deleted too.",
    });
    if (!ok) return;

    // Visuals are found by product_id rather than by walking
    // campaigns -> briefs -> visuals. That walk missed every Studio visual for
    // the product, which has a product_id but never had a brief, so those rows
    // and their files were left behind.
    const { data: visuals } = await supabase
      .from("visuals")
      .select("id")
      .eq("product_id", productId);
    if (visuals && visuals.length > 0) {
      // Clears the format variants and videos too — those files used to be
      // left in the bucket after the rows cascaded away.
      await deleteVisualsAndAssets(visuals.map((v) => v.id));
    }

    // Delete product images from storage
    const { data: imgs } = await supabase.from("product_images").select("storage_path").eq("product_id", productId);
    if (imgs && imgs.length > 0) {
      await supabase.storage.from("product-images").remove(imgs.map(i => i.storage_path));
    }
    await supabase.from("product_images").delete().eq("product_id", productId);

    const { error: prodError } = await supabase.from("products").delete().eq("id", productId);
    if (!prodError) {
      await supabase.from("projects").delete().eq("id", projectId);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
    }
  };

  const duplicateProduct = async (product: ProductListItem) => {
    if (!workspace || !user) return;

    // Create new project
    const { data: project, error: projErr } = await supabase
      .from("projects")
      .insert({
        name: `${product.name} (Copy)`,
        workspace_id: workspace.id,
        user_id: user.id,
      })
      .select()
      .single();

    if (projErr || !project) {
      return;
    }

    // Copy product
    const { data: original } = await supabase
      .from("products")
      .select("*")
      .eq("id", product.id)
      .single();

    if (original) {
      const { id, created_at, updated_at, project_id, ...rest } = original;
      await supabase.from("products").insert({
        ...rest,
        project_id: project.id,
        name: `${original.name} (Copy)`,
      });
    }

    loadProducts();
  };

  const filtered = products.filter((p) => {
    const brand = (p.product_params as any)?.brand;
    return p.name.toLowerCase().includes(search.toLowerCase()) ||
      (brand && brand.toLowerCase().includes(search.toLowerCase()));
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold">Product Library</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {products.length} product{products.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button data-tour="product-library.new" onClick={() => navigate("/app/products/new")} className="gradient-primary text-primary-foreground">
          <Plus className="w-4 h-4 mr-2" />
          New Product
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="flex items-center gap-3 mb-6">
        <div data-tour="product-library.search" className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            className="pl-9 bg-secondary border-border"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode("grid")}
            className={cn(
              "px-3 py-2 text-xs",
              viewMode === "grid" ? "bg-secondary text-foreground" : "text-muted-foreground"
            )}
          >
            Grid
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={cn(
              "px-3 py-2 text-xs",
              viewMode === "list" ? "bg-secondary text-foreground" : "text-muted-foreground"
            )}
          >
            List
          </button>
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && !search && (
        <div className="glass rounded-xl p-16 text-center">
          <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-display text-lg font-semibold mb-2">No products yet</h3>
          <p className="text-muted-foreground text-sm mb-6">
            Create your first product to start generating market-correct visuals.
          </p>
          <Button onClick={() => navigate("/app/products/new")} className="gradient-primary text-primary-foreground">
            <Plus className="w-4 h-4 mr-2" />
            Create First Product
          </Button>
        </div>
      )}

      {filtered.length === 0 && search && (
        <div className="text-center py-12 text-muted-foreground">
          No products matching "{search}"
        </div>
      )}

      {/* Product Grid */}
      {filtered.length > 0 && viewMode === "grid" && (
        <div data-tour="product-library.grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((product) => (
            <Link
              key={product.id}
              to={`/app/products/${product.id}`}
              className="glass rounded-xl p-5 hover:bg-card/80 transition-colors group"
            >
              {product.thumbnailUrl ? (
                <div className="aspect-[4/3] w-full overflow-hidden bg-secondary rounded-t-xl -mx-5 -mt-5 mb-3 p-3">
                  <img src={product.thumbnailUrl} alt="" loading="lazy" className="w-full h-full object-contain" />
                </div>
              ) : (
                <div className="aspect-[4/3] w-full bg-secondary rounded-t-xl -mx-5 -mt-5 mb-3 flex items-center justify-center">
                  <Package className="w-10 h-10 text-muted-foreground" />
                </div>
              )}
                <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div>
                    <h3 className="font-medium text-sm">{product.name}</h3>
                    {(product.product_params as any)?.brand && (
                      <p className="text-xs text-muted-foreground">{(product.product_params as any).brand}</p>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-7 h-7 opacity-0 group-hover:opacity-100"
                      onClick={(e) => e.preventDefault()}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.preventDefault()}>
                    <DropdownMenuItem onClick={() => duplicateProduct(product)}>
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => deleteProduct(product.id, product.project_id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="capitalize">{(product.product_params as any)?.product_type?.replace(/_/g, " ") || "—"}</span>
                <span>Updated {new Date(product.updated_at).toLocaleDateString()}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Product List */}
      {filtered.length > 0 && viewMode === "list" && (
        <div data-tour="product-library.grid" className="glass rounded-xl divide-y divide-border">
          {filtered.map((product) => (
            <Link
              key={product.id}
              to={`/app/products/${product.id}`}
              className="flex items-center justify-between px-5 py-4 hover:bg-card/80 transition-colors group"
            >
              <div className="flex items-center gap-3">
                {product.thumbnailUrl ? (
                  <div className="w-8 h-8 rounded overflow-hidden bg-secondary shrink-0 p-0.5">
                    <img src={product.thumbnailUrl} alt="" loading="lazy" className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <Package className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="font-medium text-sm">{product.name}</span>
                {(product.product_params as any)?.brand && (
                  <span className="text-xs text-muted-foreground">— {(product.product_params as any).brand}</span>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="capitalize">{(product.product_params as any)?.product_type?.replace(/_/g, " ") || "—"}</span>
                <span>{new Date(product.updated_at).toLocaleDateString()}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-7 h-7 opacity-0 group-hover:opacity-100"
                      onClick={(e) => e.preventDefault()}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.preventDefault()}>
                    <DropdownMenuItem onClick={() => duplicateProduct(product)}>
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => deleteProduct(product.id, product.project_id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
